import { Link } from 'react-router-dom';

import { Badge, Button, Icon } from '@/components/ui';
import { ApplyButton } from '@/features/applications/ApplyButton';
import { APPLICATION_TONES, STUDENT_APPLICATION_LABELS } from '@/features/applications/status';
import { CompanyLogo } from '@/features/internships/CompanyLogo';
import { MatchBadge } from '@/features/matching/MatchBadge';
import { MatchHighlights } from '@/features/matching/MatchBreakdown';
import { paths } from '@/routes/paths';
import { accentStyle } from '@/utils/branding';
import { cn } from '@/utils/cn';
import {
  daysUntil,
  deadlineLabel,
  durationLabel,
  placeLabel,
  postedAgo,
  stipendLabel,
} from '@/utils/format';

/**
 * The facts row: duration | stipend | place, each with an outline icon and a
 * hairline between them. One line; the last fact gives way first.
 */
function Facts({ items }) {
  const shown = items.filter((item) => item.text);
  return (
    <ul className="text-ink-muted mt-3 flex min-w-0 items-center text-[13px]" aria-label="Facts">
      {shown.map((item, index) => {
        const last = index === shown.length - 1;
        return (
          <li key={item.icon} className={cn('flex items-center', last ? 'min-w-0' : 'shrink-0')}>
            {index > 0 && <span aria-hidden="true" className="bg-line mx-3 h-3.5 w-px shrink-0" />}
            <Icon name={item.icon} size={15} className="text-ink-muted/70 mr-1.5" />
            <span className={cn(last && 'truncate')} title={last ? item.text : undefined}>
              {item.text}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * One internship, laid out like a job-board result: title and company on
 * the left with the logo on the right, a row of facts, a one-line preview of
 * the description, the skills as plain text, and a footer with when it was
 * posted, Save and Apply.
 *
 * The one card for every list of internships -- discovery, "For you", the
 * saved list and the career page -- so they all read alike.
 *
 * - `match`: the student's fit; shows the match badge and its highlights.
 * - `application`: `{ id, status }` when the student has applied ("For you"
 *   sends it). The footer then shows the stage and links to the application.
 * - `children`: anything else, full width above the footer (a saved note).
 *
 * The whole card opens the listing: the title's link stretches over it, and
 * the buttons sit above that layer so they never trigger it.
 */
export function InternshipCard({
  internship,
  onToggleSave,
  isSaving = false,
  match,
  application,
  children,
}) {
  const { company } = internship;
  const skills = internship.skills ?? [];
  const detail = paths.internshipDetail(internship.slug);
  const days = daysUntil(internship.application_deadline);
  const urgent = !internship.is_open || (days !== null && days < 3);
  const posted = postedAgo(internship.published_at);
  const stageTone = application ? (APPLICATION_TONES[application.status] ?? 'neutral') : null;

  return (
    <article
      className="group border-line bg-surface hover:border-brand-200 relative rounded-[16px] border p-5 shadow-[0_1px_2px_rgb(30_20_60/0.05),0_6px_20px_-8px_rgb(30_20_60/0.12)] transition hover:shadow-[0_2px_4px_rgb(30_20_60/0.06),0_12px_28px_-10px_rgb(30_20_60/0.18)] sm:p-6"
      style={accentStyle(company.accent_color)}
    >
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="text-ink truncate text-[17px] leading-snug font-semibold">
            <Link
              to={detail}
              title={internship.title}
              className="group-hover:text-brand-700 rounded-sm transition after:absolute after:inset-0 after:rounded-[16px] after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-brand-500"
            >
              {internship.title}
            </Link>
          </h3>
          <p className="text-ink-muted mt-1 flex min-w-0 items-center gap-1.5 text-sm font-medium">
            <span className="truncate" title={company.name}>
              {company.name}
            </span>
            {company.is_verified && (
              <span
                title="Verified company"
                className="bg-success-500 grid size-4 shrink-0 place-items-center rounded-full text-white"
              >
                <Icon name="check" size={11} />
                <span className="sr-only">Verified company</span>
              </span>
            )}
          </p>
          {match && <MatchBadge match={match} className="mt-2" />}
        </div>
        <CompanyLogo company={company} />
      </div>

      <Facts
        items={[
          { icon: 'clockOutline', text: durationLabel(internship.duration_months) },
          { icon: 'banknote', text: stipendLabel(internship) },
          { icon: 'mapPin', text: placeLabel(internship) },
        ]}
      />

      {internship.summary && (
        <p className="text-ink-muted mt-2.5 flex min-w-0 items-center gap-1.5 text-[13px]">
          <Icon name="document" size={15} className="text-ink-muted/70" />
          <span className="truncate">{internship.summary}</span>
        </p>
      )}

      {skills.length > 0 && (
        <p
          className="text-ink-muted mt-2 truncate text-[13px]"
          aria-label="Skills"
          title={skills.join(', ')}
        >
          {skills.join(' · ')}
        </p>
      )}

      {match && <MatchHighlights match={match} className="mt-3" />}
      {children}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <p className="text-ink-muted min-w-0 truncate text-xs">
          {posted}
          {internship.application_deadline && (
            <span className={cn(urgent && 'text-danger-600 font-medium')}>
              {posted && ' · '}
              {deadlineLabel(internship.application_deadline)}
            </span>
          )}
        </p>

        {/* Above the card's link layer, so these never open the listing. */}
        <div className="relative z-10 ml-auto flex shrink-0 items-center gap-2">
          {onToggleSave && (
            <Button
              variant="ghost"
              size="xs"
              className={cn('gap-1.5 text-[13px]', internship.is_saved && 'text-brand-700')}
              isLoading={isSaving}
              onClick={() => onToggleSave(internship)}
              aria-pressed={Boolean(internship.is_saved)}
              aria-label={internship.is_saved ? 'Saved — remove from saved' : 'Save'}
            >
              {!isSaving && <Icon name="bookmark" size={16} filled={internship.is_saved} />}
              {internship.is_saved ? 'Saved' : 'Save'}
            </Button>
          )}
          <ApplyAction internship={internship} application={application} stageTone={stageTone} />
        </div>
      </div>
    </article>
  );
}

/**
 * The footer's main action: Apply while it can be done, the stage (linking to
 * the application) once it has been, and nothing once applications close.
 */
function ApplyAction({ internship, application, stageTone }) {
  if (application) {
    return (
      <Link
        to={paths.student.applicationDetail(application.id)}
        title="View application"
        className="rounded-full"
      >
        <Badge tone={stageTone} dot>
          {STUDENT_APPLICATION_LABELS[application.status] ?? 'Applied'}
        </Badge>
      </Link>
    );
  }
  if (internship.has_applied) {
    return (
      <Badge tone="success">
        <Icon name="check" size={12} />
        Applied
      </Badge>
    );
  }
  if (!internship.is_open) return null;
  // The same rules and dialog as the listing's own page.
  return <ApplyButton internship={internship} />;
}
